import classNames from "classnames";
import { useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import type { UserAssetRecord } from "@apis/http/api";
import { IconLoading } from "@icons/index";
import { TestId } from "@shared/testIds";
import { GENERATED_CARD_HEIGHT, LIBRARY_GRID_WIDTH, type PostVideoLibrarySubTab } from "./constants";

const librarySubTabTestId: Record<PostVideoLibrarySubTab, TestId> = {
  generated: TestId.PostVideoLibrarySubTabGenerated,
  uploaded: TestId.PostVideoLibrarySubTabUploaded,
};
import { PostVideoLibraryCard } from "./PostVideoLibraryCard";
import { getPostVideoAssetListKey } from "./postVideoLibraryAsset";
import { usePostVideoLibrary } from "./usePostVideoLibrary";

const LIBRARY_SUB_TABS: { key: PostVideoLibrarySubTab; label: string }[] = [
  { key: "generated", label: "Generated" },
  { key: "uploaded", label: "Uploaded" },
];

export interface PostVideoLibraryPanelProps {
  enabled: boolean;
  subTab: PostVideoLibrarySubTab;
  onSubTabChange: (tab: PostVideoLibrarySubTab) => void;
  selectedId?: number;
  onSelect: (item: UserAssetRecord | undefined) => void;
}

export const PostVideoLibraryPanel = ({
  enabled,
  subTab,
  onSubTabChange,
  selectedId,
  onSelect,
}: PostVideoLibraryPanelProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const library = usePostVideoLibrary({ enabled, subTab });

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = library.scrollTop;
  }, [library.scrollTop, subTab]);

  const handleSubTabChange = (tab: PostVideoLibrarySubTab) => {
    onSubTabChange(tab);
    onSelect(undefined);
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-col items-start px-[24px] py-[12px]">
        <div className="flex h-[32px] items-center gap-[32px]">
          {LIBRARY_SUB_TABS.map((tab) => {
            const isActive = tab.key === subTab;
            return (
              <button
                key={tab.key}
                type="button"
                data-testid={librarySubTabTestId[tab.key]}
                onClick={() => handleSubTabChange(tab.key)}
                className={classNames(
                  "flex cursor-pointer items-center justify-center py-[4px] transition-colors",
                  isActive ? "text-kds-fg" : "text-kds-fg-subtle hover:text-kds-fg-soft",
                )}
              >
                <span className="whitespace-nowrap text-[16px] font-semibold leading-[24px]">
                  <Trans>{tab.label}</Trans>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        ref={listRef}
        className="max-h-[480px] min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-[16px] pb-[24px]"
        onScroll={(event) => library.saveScrollTop(subTab, event.currentTarget.scrollTop)}
      >
        {library.loading ? (
          <div
            className="flex w-full items-center justify-center text-kds-fg-soft"
            style={{ height: GENERATED_CARD_HEIGHT }}
          >
            <IconLoading className="size-[24px] animate-spin" />
          </div>
        ) : library.error ? (
          <div
            className="flex w-full flex-col items-center justify-center gap-[12px] text-[14px] text-kds-fg-soft"
            style={{ height: GENERATED_CARD_HEIGHT }}
          >
            <Trans>Load failed.</Trans>
            <button
              type="button"
              onClick={library.refresh}
              className="rounded-[8px] border border-kds-border px-[12px] py-[6px] text-kds-fg transition-colors hover:border-kds-accent"
            >
              <Trans>Retry</Trans>
            </button>
          </div>
        ) : library.items.length > 0 ? (
          <div className="mx-auto grid grid-cols-4 gap-[16px]" style={{ width: LIBRARY_GRID_WIDTH }}>
            {library.items.map((record) => (
              <PostVideoLibraryCard
                key={getPostVideoAssetListKey(record)}
                record={record}
                subTab={subTab}
                selected={selectedId === record.id}
                onSelect={onSelect}
              />
            ))}
            {library.hasMore && <div ref={library.sentinelRef} className="col-span-4 h-px w-full" />}
            {library.loadingMore && (
              <div className="col-span-4 flex h-[32px] w-full items-center justify-center text-kds-fg-soft">
                <IconLoading className="size-[18px] animate-spin" />
              </div>
            )}
          </div>
        ) : (
          <div
            className="flex w-full items-center justify-center text-[14px] text-kds-fg-soft"
            style={{ height: GENERATED_CARD_HEIGHT }}
          >
            <Trans>Nothing here yet.</Trans>
          </div>
        )}
      </div>
    </div>
  );
};
